/** The color engine runs locally in a cancellable worker. Progress names real
 * stages; it never invents a theatrical percentage. */

import { useEffect, useRef, useState } from "react";

import { copy, splitSharedBrief } from "@core/index.js";

import { useAnnounce } from "../app/announcer";
import { useStudio } from "../app/store";
import { AnalysisCancelled } from "../image/messages";
import { createColourRunner, type ColourRunner } from "../image/workerClient";

type StageState = "pending" | "active" | "done";

export const Processing = ({ runner, onCancelled }: { runner?: ColourRunner; onCancelled?: () => void }) => {
  const { state, dispatch } = useStudio();
  const announce = useAnnounce();
  const runnerRef = useRef<ColourRunner | null>(null);
  const [stages, setStages] = useState<StageState[]>(["pending", "pending", "pending", "pending"]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!state.source) {
      setError("Choose an image first. Nothing else was lost.");
      return () => { alive = false; };
    }
    const source = state.source;
    const active = runner ?? createColourRunner();
    runnerRef.current = active;
    dispatch({ type: "sourceStatus", status: "analyzing" });
    const stageIndex = { normalizing: 0, hashing: 1, clustering: 2, building: 3 } as const;

    void active.run(
      {
        width: source.workingWidth,
        height: source.workingHeight,
        rgba: source.rgba.slice(0),
        answers: splitSharedBrief(state.brief).colour,
        crop: source.crop,
        ...(source.alphaGround ? { alphaGround: source.alphaGround } : {}),
        protectedHexes: source.protectedHexes,
      },
      (stage) => {
        if (!alive) return;
        setStages((current) => current.map((_value, index) => index < stageIndex[stage] ? "done" : index === stageIndex[stage] ? "active" : "pending"));
      },
    ).then((output) => {
      if (!alive) return;
      setStages(["done", "done", "done", "done"]);
      dispatch({ type: "analysisReady", analysis: output });
      dispatch({ type: "colourReady", run: { systems: output.systems, companionSystems: output.companionSystems, recommendation: output.recommendation } });
      dispatch({ type: "setStep", step: "direction" });
      announce("Your color worlds are ready.");
    }).catch((cause: unknown) => {
      if (!alive) return;
      if (cause instanceof AnalysisCancelled) {
        dispatch({ type: "sourceStatus", status: "ready" });
        announce("Stopped. Your image is still here.");
        onCancelled?.();
        return;
      }
      dispatch({ type: "sourceStatus", status: "error", error: cause instanceof Error ? cause.message : "Color analysis failed." });
      setError(cause instanceof Error ? cause.message : "Color analysis failed.");
    });

    return () => {
      alive = false;
      active.cancel();
    };
    // The processing step deliberately captures one immutable run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div aria-busy={error ? undefined : "true"}>
      <p className="eyebrow">Listening to the image</p>
      <h2 className="step-title">Finding the colors that can do the work.</h2>
      <p className="step-intro">{copy.processing.noPercent}</p>
      <ol className="stage-list" aria-label="Color analysis stages">
        {copy.processing.image.map((label, index) => <li key={label} data-state={stages[index] ?? "pending"}>{label}</li>)}
      </ol>
      {error ? (
        <div className="card" role="alert" style={{ marginTop: "1.4rem", borderColor: "var(--blocking)" }}>
          <h3>The work stopped. Your image did not vanish.</h3>
          <p>{error}</p>
          <div className="flow-actions">
            <button type="button" className="btn btn--primary" onClick={() => dispatch({ type: "setStep", step: "source" })}>Back to my image</button>
            <button type="button" className="btn" onClick={() => dispatch({ type: "setStep", step: "brief" })}>Check my answers</button>
          </div>
        </div>
      ) : (
        <p className="processing-cancel"><button type="button" className="btn" onClick={() => runnerRef.current?.cancel()}>Cancel and keep my image</button></p>
      )}
    </div>
  );
};
